import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

import FormField from '@/components/atoms/FormField';
import StyledSingleDatePicker from '@/components/atoms/StyledSingleDatePicker/StyledSingleDatePicker';
import TextField from '@/components/atoms/TextField';

import { toTimeValue, withDay, withTime } from './movedAt';

import styles from './BioscopeDetailMovingBioscopeModal.module.scss';

interface MovedAtFieldProps {
    value: Date | null;
    onChange: (movedAt: Date | null) => void;
}

/** The move time, edited as a day and a time of day. */
export function MovedAtField({ value, onChange }: MovedAtFieldProps) {
    const { t } = useTranslation();
    // A day being typed reports no day until it is complete, which empties `value`; the last
    // full move time keeps the time of day for when the day arrives.
    const [lastMovedAt, setLastMovedAt] = useState<Date | null>(value);
    // The time sits in the day's field, so it needs an id of its own rather than the field's.
    const timeInputId = useId().replace(/:/g, '');

    function setMovedAt(movedAt: Date | null): void {
        if (movedAt !== null) setLastMovedAt(movedAt);
        onChange(movedAt);
    }

    return (
        <FormField label={t('molecules.bioscopeDetailMovingBioscopeModal.whenWasItMoved')}>
            <div className={styles.dateTimeRow}>
                <div className={styles.date}>
                    <StyledSingleDatePicker
                        date={value}
                        onDateChange={(day) =>
                            setMovedAt(day === null ? null : withDay(day, value ?? lastMovedAt))
                        }
                    />
                </div>
                <TextField
                    id={timeInputId}
                    aria-label={t('molecules.bioscopeDetailMovingBioscopeModal.whenWasItMoved')}
                    type="time"
                    className={styles.time}
                    value={toTimeValue(value)}
                    onChange={(ev) => {
                        const movedAt = withTime(ev.target.value, value ?? lastMovedAt);
                        if (movedAt !== null) {
                            setMovedAt(movedAt);
                        }
                    }}
                />
            </div>
        </FormField>
    );
}
